import {
  Badge,
  BadgeVariantEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  Column,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  matchString,
  Table,
  TableOptions,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { TableRowImage } from '@/components/TableRowImage';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { groupItemsById } from '@/features/broadcast-schedule-program/helpers/group-by-id';
import { TClipDeclaration } from '@/features/clips/types';
import { usePeopleById } from '@/hooks/use-catalogue-entries-by-id';
import { getOffsetHHMMSS } from '@/utils/hh-mm-ss-into-number';

import { ClipToolkitDeclarationsSubTable } from './ClipToolkitDeclarationsSubTable';

type ClipToolkitDeclarationsTableProps = {
  items: TClipDeclaration[];
  onPreview?: (item: TClipDeclaration) => void;
  onUpdate?: (item: TClipDeclaration) => void;
  onDelete?: (item: TClipDeclaration) => void;
};

export function ClipToolkitDeclarationsTable({
  items,
  onPreview,
  onUpdate,
  onDelete,
}: ClipToolkitDeclarationsTableProps) {
  const { activeClip } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { byId: peopleById } = usePeopleById(
    items.map(item => item.people_id),
    { keepPrevious: true }
  );

  const groupedPeople = useMemo(() => groupItemsById({ items, idKey: 'people_id' }), [items]);

  const options: Partial<TableOptions> = {
    initialSortBy: { id: 'updated_at', order: 'desc' },
    filters: [
      {
        name: 'fiable',
        placeholder: t('Grado de fiabilidad'),
        options: [],
        onChange: () => {
          return groupedPeople;
        },
      },
      {
        name: 'edt',
        placeholder: t('Asignado a EDT'),
        options: [],
        onChange: () => {
          return groupedPeople;
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por persona'),
  };

  const columns: Column<TClipDeclaration>[] = useMemo(
    () => [
      {
        id: 'persona',
        title: t('Persona'),
        searchable: true,
        matchFn: (query, item) => {
          let label = item?.entity_label;
          if (!label && (item as any)?.children?.length) {
            label = (item as any)?.children[0].entity_label;
          }

          return matchString((label ?? '').toString(), query);
        },
        render: item => {
          const selectedID = item.people_id || item.id;
          const people = selectedID ? peopleById.get(selectedID) : undefined;
          const storedLabel = item.entity_label || (item as any)?.children?.[0]?.entity_label || '';
          return (
            <TableRowImage
              source={people?.parameters?.main_logo_presigned_url || '/default-logo.svg' || ''}
              alt={people?.name || storedLabel}
              name={people?.name || storedLabel}
            />
          );
        },
      },
      {
        id: 'declaracion',
        title: t('Declaración'),
        render: item => {
          return <TableRow content={item.text || ''} />;
        },
      },
      {
        id: 'timeline',
        title: t('Timeline'),
        render: item => {
          if (!activeClip || !item.start_datetime) return <></>;

          const seconds = getOffsetHHMMSS(item.start_datetime, activeClip.start_datetime);

          return item?.children && item.children?.length > 0 ? (
            <Badge variant={BadgeVariantEnum.DefaultFilled}>{item.children.length}</Badge>
          ) : (
            <TableRow content={seconds} />
          );
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: (item, _index, _rows, showSubArrows, subArrowsAreVisible) => (
          <div className="inline-flex w-full justify-end">
            {item?.children && item.children?.length > 0 ? (
              <>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton
                  color={IconButtonColorEnum.NONE_DEFAULT}
                  shape={IconButtonShapeEnumOld.OLD_SQUARED}
                  onClick={() => showSubArrows(prevVal => !prevVal)}
                >
                  {!subArrowsAreVisible ? (
                    <ChevronDownIcon size={IconSizeEnum.XS} />
                  ) : (
                    <ChevronUpIcon size={IconSizeEnum.XS} />
                  )}
                </IconButton>
              </>
            ) : (
              <RenderActionButtons
                item={item}
                onPreview={() => onPreview?.(item)}
                onUpdate={() => onUpdate?.(item)}
                onDelete={() => onDelete?.(item)}
              />
            )}
          </div>
        ),
      },
    ],
    [t, items, peopleById]
  );

  return (
    <Table<any>
      options={options}
      columns={columns}
      data={groupedPeople}
      renderAdditionalRow={(item: TClipDeclaration) => (
        <ClipToolkitDeclarationsSubTable
          item={item}
          columns={columns}
          onPreview={onPreview}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      )}
      ignoreWrap
    />
  );
}
